import { useState, type RefObject } from 'react';
import { useCompanions, useSetActive, type CompanionDto } from '../api/companions';
import { Dialog } from '../components/BottomSheet';
import { Aside } from '../components/Aside';
import { useOnline } from '../pwa/useOnline';

const GENERIC_ERROR = 'Something went wrong — please try again.';

export type CompanionPickerSheetProps = {
  /** The companion currently in the den — marked, and a no-op to tap. */
  activeId: number;
  onClose: () => void;
  /** What picking a companion MEANS, when it is not "call them into the den". The wardrobe passes
   *  a navigation (2026-09-14, finding 9): there, the question is only whose wardrobe to open, and
   *  `/wardrobe/:id` answers it with no server call — a player dressing a second pet has not
   *  thereby moved it into the den. Left undefined from Habitat, which keeps `PUT
   *  /companions/{id}/active`. The active-companion chip and the no-op tap on the active row are
   *  the same either way, which is why this is one prop rather than a second sheet. */
  onPick?: (companion: CompanionDto) => void;
  /** The chip on the current row. "In the den" is literally true on Habitat and literally false in
   *  the wardrobe, which may be dressing a pet that is not in the den at all — the row still has
   *  to be marked, so the word is the caller's. */
  currentLabel?: string;
  restoreFocusRef?: RefObject<HTMLElement | null>;
  portalContainer?: HTMLElement | null;
};

/**
 * SCREENS.md "/habitat — companion picker" (2026-09-14). The one place a keeper with more than one
 * companion chooses who is in the den. Opened from the identity bar's name; lists every companion
 * as a row (name, "species · stage", level), marks the active one "In the den", and a tap on any
 * other calls `PUT /companions/{id}/active` — the same mutation the Phase 1 strip used, just
 * reachable now.
 *
 * WHY A SHEET AND NOT THE OLD PER-COMPANION STRIP. `HabitatScreen` used to render a "name + Set
 * active" row under the scene for each inactive companion. The 2026-08-20 layout overhaul made the
 * scene full-bleed with a floating dock and never accounted for those rows, so on a real phone they
 * sat UNDER the dock (measured at 412x839: y=751..839, page not scrollable) — a second pet's only
 * switch was unreachable. SCREENS.md's own /habitat row already said "inactive companions are not
 * in this room"; this sheet is the door they come in through.
 *
 * `max === 1` radio feel, same as `PartyPickerSheet`'s single-seat mode — but this is not that
 * sheet: it has no confirm step (tapping IS the choice; a mistaken tap is one more tap to undo, and
 * nothing is spent), no level gate, and no party. Tapping the companion already in the den simply
 * closes the sheet without a request.
 */
export function CompanionPickerSheet({
  activeId, onClose, onPick, currentLabel = 'In the den', restoreFocusRef, portalContainer,
}: CompanionPickerSheetProps) {
  const companionsQuery = useCompanions();
  const setActive = useSetActive();
  const isOnline = useOnline();
  const [error, setError] = useState<string | null>(null);

  const companions = companionsQuery.data ?? [];

  function pick(c: CompanionDto) {
    if (c.id === activeId) { onClose(); return; }
    if (onPick) { onPick(c); return; }
    if (setActive.isPending) return;
    setError(null);
    setActive.mutate(c.id, {
      onSuccess: () => onClose(),
      onError: () => setError(GENERIC_ERROR),
    });
  }

  return (
    <Aside
      title="Your companions"
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      contentClassName="companion-picker-sheet"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
    >
      <div className="sheet-header">
        <Dialog.Title asChild><h2>Your companions</h2></Dialog.Title>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close">Close</button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">Choose which companion is in the den.</Dialog.Description>

      <div className="sheet-body">
        {companionsQuery.isError ? (
          <div className="retry-card">
            <p>Couldn&rsquo;t reach Project Pets.</p>
            <button type="button" className="btn-primary" onClick={() => companionsQuery.refetch()}>Try again</button>
          </div>
        ) : (
          <>
            <ul className="companion-picker-list">
              {companions.map((c) => {
                const inDen = c.id === activeId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={`companion-picker-row${inDen ? ' chip-active' : ''}`}
                      aria-current={inDen ? 'true' : undefined}
                      disabled={!isOnline || setActive.isPending}
                      onClick={() => pick(c)}
                    >
                      <span className="companion-picker-info">
                        <span className="companion-picker-name">{c.name}</span>
                        <span className="companion-picker-sub">{c.speciesName} · {c.stage}</span>
                      </span>
                      <span className="chip">Lv {c.level}</span>
                      {inDen && <span className="chip companion-picker-here">{currentLabel}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
            {setActive.isPending && <p className="form-note">Calling them in…</p>}
            {error && <p className="field-error" role="alert">{error}</p>}
          </>
        )}
      </div>
    </Aside>
  );
}
